export function buildHierarchy(entries) {
  const rows = entries.filter(Boolean);
  const nodes = new Map(rows.map(row => [row.handle, row]));
  const children = new Map();
  for (const row of rows) {
    if (!row.parent || row.parent === row.handle || !nodes.has(row.parent)) continue;
    if (!children.has(row.parent)) children.set(row.parent, new Set());
    children.get(row.parent).add(row.handle);
  }
  const roots = [...new Set(rows.filter(row => row.source === 'main' && !row.parent).map(row => row.handle))];
  const incoming = new Set([...children.values()].flatMap(set => [...set]));
  for (const handle of nodes.keys()) if (!incoming.has(handle) && !roots.includes(handle)) roots.push(handle);
  const paths = new Map();
  const queue = roots.map(handle => [handle]);
  for (let index = 0; index < queue.length; index++) {
    const path = queue[index];
    const handle = path.at(-1);
    if (paths.has(handle)) continue;
    paths.set(handle, path);
    if (path.length < 8) for (const child of children.get(handle) || []) {
      if (!path.includes(child)) queue.push([...path, child]);
    }
  }
  const valid = path => Array.isArray(path) && path.length > 0 && path.length <= 8 &&
    new Set(path).size === path.length && roots.includes(path[0]) &&
    path.every((handle, index) => nodes.has(handle) && (!index || children.get(path[index - 1])?.has(handle)));
  return { rows, nodes, children, roots, paths, valid };
}

export function resolvePath(graph, data, requested = '') {
  const memberships = data.memberships || [];
  const isProduct = data.type === 'product';
  const contextual = data.collection && (!isProduct || memberships.includes(data.collection)) ? data.collection : '';
  const input = requested.split(',').filter(Boolean);
  const allowed = isProduct ? memberships : [data.collection];
  if (graph.valid(input) && allowed.includes(input.at(-1)) && (!contextual || input.at(-1) === contextual)) return input;
  if (contextual) return graph.paths.get(contextual) || [contextual];
  if (isProduct) {
    let best = [];
    for (const member of memberships) {
      const candidate = graph.paths.get(member) || [];
      if (candidate.length > best.length) best = candidate;
    }
    return best;
  }
  return [];
}

export function resolveParent(graph, data, currentPath, requested = '') {
  if (data.type === 'product') {
    const input = requested.split(',').filter(Boolean);
    const explicit = data.collection && (data.memberships || []).includes(data.collection);
    const verifiedRequest = graph.valid(input) && (data.memberships || []).includes(input.at(-1));
    if (!explicit && !verifiedRequest) return [];
    return currentPath;
  }
  return data.type === 'collection' ? currentPath.slice(0, -1) : [];
}

function start() {
  const source = document.getElementById('lian-breadcrumb-data');
  if (!source) return;
  let data;
  try { data = JSON.parse(source.textContent); } catch { return; }
  const graph = buildHierarchy(data.entries);
  let currentPath = resolvePath(graph, data, new URL(location.href).searchParams.get('category_path') || '');
  const cleanLabel = value => value.replace(/\s+/g, ' ').trim().toLocaleLowerCase();
  const collectionHandle = url => url.pathname.match(/\/collections\/([^/]+)\/?$/)?.[1] || '';
  const pathUrl = (handle, path) => {
    const url = new URL(graph.nodes.get(handle)?.url || `${data.collectionsUrl}/${handle}`, location.origin);
    if (graph.valid(path)) url.searchParams.set('category_path', path.join(','));
    return url.pathname + url.search + url.hash;
  };
  function menuPath(anchor, handle) {
    const parents = [];
    const readHandle = href => {
      try { return collectionHandle(new URL(href, location.origin)); } catch { return ''; }
    };
    let dropdown = anchor.closest('ul.slide-nav__dropdown');
    if (dropdown) {
      while (dropdown) {
        const viewAll = dropdown.querySelector(':scope > li > a[href*="/collections/"]');
        const parent = readHandle(viewAll?.getAttribute('href'));
        if (parent) parents.unshift(parent);
        dropdown = dropdown.parentElement.closest('ul.slide-nav__dropdown');
      }
    } else {
      const summary = anchor.closest('details.site-nav__details')?.querySelector(':scope > summary[data-link]');
      const root = readHandle(summary?.getAttribute('data-link'));
      if (root) parents.push(root);
      const column = anchor.parentElement.matches('.megamenu__col-title') ? anchor.parentElement.parentElement : anchor.parentElement;
      const heading = column.querySelector(':scope > .megamenu__col-title > a');
      const parent = readHandle(heading?.getAttribute('href'));
      if (parent) parents.push(parent);
    }
    const path = [...new Set([...parents, handle])];
    return graph.valid(path) ? path : null;
  }
  function render() {
    currentPath = resolvePath(graph, data, new URL(location.href).searchParams.get('category_path') || '');
    for (const nav of document.querySelectorAll('[data-lian-breadcrumbs]')) {
      const list = nav.querySelector('ol');
      const back = nav.querySelector('[data-lian-parent-link]');
      if (back) {
        const parentPath = resolveParent(graph, data, currentPath, new URL(location.href).searchParams.get('category_path') || '');
        const parent = graph.nodes.get(parentPath.at(-1));
        back.href = parent ? pathUrl(parent.handle, parentPath) : data.collectionsUrl;
        back.textContent = parent ? `${data.backTo || 'Back to'} ${parent.title}` : (data.browseCollections || 'Browse collections');
      }
      if (!list || !currentPath.length) continue;
      const fragment = document.createDocumentFragment();
      const add = (title, href, current = false) => {
        const item = document.createElement('li');
        const element = document.createElement(href ? 'a' : 'span');
        element.textContent = title;
        if (href) element.setAttribute('href', href);
        if (current) element.setAttribute('aria-current', 'page');
        item.append(element);
        fragment.append(item);
      };
      add(data.home, data.root);
      for (let index = 0; index < currentPath.length; index++) {
        const handle = currentPath[index];
        const node = graph.nodes.get(handle);
        const existing = list.querySelector(`[data-lian-category="${CSS.escape(handle)}"]`);
        const title = node?.title || existing?.textContent.trim();
        if (!title) continue;
        const current = data.type === 'collection' && index === currentPath.length - 1 && !data.tags?.length;
        add(title, current ? null : pathUrl(handle, currentPath.slice(0, index + 1)), current);
      }
      if (data.type === 'product') add(data.productTitle, null, true);
      else if (data.tags?.length) add(data.tags.join(' + '), null, true);
      list.replaceChildren(fragment);
      list.classList.remove('element-breadcrumbs--no-current-page');
    }
  }
  function enhance(anchor) {
    if (!anchor.href || anchor.getAttribute('href')?.startsWith('#') || anchor.closest('[data-lian-breadcrumbs]') || anchor.hasAttribute('download')) return;
    let url;
    try { url = new URL(anchor.href); } catch { return; }
    if (url.origin !== location.origin) return;
    const handle = collectionHandle(url);
    let path;
    if (handle && graph.nodes.has(handle)) {
      const current = currentPath.at(-1);
      if (anchor.closest('#MainContent') && current && (handle === current || graph.children.get(current)?.has(handle))) {
        path = handle === current ? currentPath : [...currentPath, handle];
      } else {
        path = menuPath(anchor, handle);
        const matches = graph.rows.filter(row => row.source === 'main' && row.handle === handle && cleanLabel(row.label) === cleanLabel(anchor.textContent));
        const parent = matches[0]?.parent;
        if (!path && matches.length && parent && graph.paths.has(parent)) path = [...graph.paths.get(parent), handle];
        if (!path) path = graph.paths.get(handle);
      }
    } else if (/\/products\/[^/]+\/?$/.test(url.pathname) && anchor.closest('#MainContent') && currentPath.length) {
      path = currentPath;
    }
    if (!graph.valid(path)) return;
    url.searchParams.set('category_path', path.join(','));
    const href = url.pathname + url.search + url.hash;
    if (anchor.getAttribute('href') !== href) anchor.setAttribute('href', href);
  }
  const refresh = () => document.querySelectorAll('a[href]').forEach(enhance);
  render();
  refresh();
  for (const event of ['pointerover', 'focusin', 'click', 'auxclick']) document.addEventListener(event, event => {
    const anchor = event.target.closest?.('a[href]');
    if (anchor) enhance(anchor);
  }, true);
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; refresh(); });
  }).observe(document.body, { childList: true, subtree: true });
  addEventListener('popstate', () => { render(); refresh(); });
}

if (typeof document !== 'undefined') start();
